import { skipToken } from '@apollo/client/react'
import { produce } from 'immer'
import type { ListMessagesQuery } from './generated/graphql'
import {
  DeleteMessageDocument,
  GetMessageDocument,
  GetNeedCommentTurnstileDocument,
  ListMessagesDocument,
  MessageBaseFragmentDoc,
  SendMessageDocument,
  SendMessageV2Document,
} from './generated/graphql'
import {
  createLocalCursor,
  createMutationHelper,
  createQueryHook,
  infiniteQueryHook,
  modifyQueryHook,
} from './utils'

export const useGetMessage = modifyQueryHook(
  createQueryHook(GetMessageDocument),
  {
    transform: (id: string) => ({ id }),
    reduce: r => r?.message,
  },
)

export const useInfiniteListMessages = infiniteQueryHook(
  modifyQueryHook(createQueryHook(ListMessagesDocument), {
    reduce: r => r?.messages,
  }),
)

export const [useSendMessage, executeSendMessage] = createMutationHelper(
  SendMessageDocument,
  {
    update: (cache, result) => {
      const sendMessage = result.data?.sendMessage
      if (sendMessage) {
        cache.updateQuery(
          {
            query: ListMessagesDocument,
            variables: { topicId: sendMessage.topicId },
          },
          produce((data: ListMessagesQuery | null) => {
            if (data == null) {
              return data
            }
            const cursor = createLocalCursor()
            data.messages.edges?.push({ node: sendMessage, cursor })
            data.messages.pageInfo.endCursor = cursor
          }),
        )
      }
    },
  },
)
export const [useSendMessageV2, executeSendMessageV2] = createMutationHelper(
  SendMessageV2Document,
  {
    update: (cache, result) => {
      const sendMessage = result.data?.sendMessageV2
      if (sendMessage) {
        cache.updateQuery(
          {
            query: ListMessagesDocument,
            variables: { topicId: sendMessage.topicId },
          },
          produce((data: ListMessagesQuery | null) => {
            if (data == null) {
              return data
            }
            const cursor = createLocalCursor()
            data.messages.edges?.push({ node: sendMessage, cursor })
            data.messages.pageInfo.endCursor = cursor
          }),
        )
      }
    },
  },
)
export const [useDeleteMessage, executeDeleteMessage] = createMutationHelper(
  DeleteMessageDocument,
  {
    update: (cache, result, { variables }) => {
      const error = result.errors
      const messageId = variables?.messageId
      if (messageId == undefined) {
        return
      }
      if ((error?.length ?? 0) <= 0) {
        const messageCacheId = cache.identify({
          __typename: 'Message',
          id: messageId,
        })
        const message = cache.readFragment({
          fragment: MessageBaseFragmentDoc,
          fragmentName: 'MessageBase',
          id: messageCacheId,
        })
        if (message) {
          cache.updateQuery(
            {
              query: ListMessagesDocument,
              variables: { topicId: message.topicId },
            },
            produce((data: ListMessagesQuery | null) => {
              if (data == null) {
                return data
              }
              for (const [idx, edge] of data.messages.edges?.entries() ?? []) {
                if (edge?.node?.id === messageId) {
                  data.messages.edges?.splice(idx, 1)
                  break
                }
              }
              data.messages.pageInfo.endCursor =
                data.messages.edges?.at(-1)?.cursor
              data.messages.pageInfo.startCursor =
                data.messages.edges?.at(0)?.cursor
            }),
          )
        }
        cache.evict({ id: messageCacheId })
        cache.gc()
      }
    },
  },
)

const _useGetNeedCommentTurnstile = modifyQueryHook(
  createQueryHook(GetNeedCommentTurnstileDocument),
  { reduce: r => r?.needCommentTurnstile ?? false },
)

export const useNeedCommentTurnstile = (skip: boolean) =>
  _useGetNeedCommentTurnstile(skip ? skipToken : {})
